/**
 * API client — axios wrapper with the v0.5.0 auth interceptor.
 *
 * Two modes, both with the same external shape (callers use `getApiClient().get/.post/.put/.patch`):
 *
 * - **Bypass** (VITE_AUTH_BYPASS=true): attaches a static `dev-bypass-token`
 *   bearer. The dashboard API's BypassVerifier returns a fake
 *   RCM_OPS_REVIEWER identity. No refresh logic; no error mapping —
 *   responses pass through as raw axios errors. Dev-only.
 *
 * - **Cognito** (VITE_AUTH_BYPASS=false): pulls the ID token from Amplify
 *   on every request, attaches it as `Authorization: Bearer <id token>`,
 *   and implements the v0.5.0 error contract:
 *
 *     401 + body.code === "TENSAW_BASE_ACCESS_TOKEN_EXPIRED"
 *       → silently refresh + retry the original request once. If the retry
 *         also 401s, or refresh itself fails, throws TerminalAuthError.
 *
 *     401 (any other code)
 *       → throws TerminalAuthError. The global handler (main.tsx) signs out.
 *
 *     403  → throws AccessDeniedError. The route renders an access-denied
 *            UI (clinic-scope inline card, role-level full-page guard).
 *            Does NOT sign the user out — their session is valid.
 *
 *     503 + body.code === "TENSAW_BASE_UPSTREAM_UNAVAILABLE"
 *       → throws UpstreamUnavailableError. TanStack Query retries 5xx; this
 *            keeps users from being kicked during a JWKS/IdP blip.
 *
 *     Anything else → re-thrown as-is.
 *
 * The single in-flight refresh promise (`refreshLock`) coalesces concurrent
 * 401s so the first hit triggers refresh and subsequent ones wait on the
 * same promise — important because TanStack Query fires N parallel
 * requests on mount.
 */
import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { getIdToken } from "@/auth/tokenProvider";
import {
  AccessDeniedError,
  TerminalAuthError,
  UpstreamUnavailableError,
} from "@/auth/errors";

let client: AxiosInstance | null = null;
let authBypassActive = false;

export interface ApiClientConfig {
  baseURL: string;
  authBypass: boolean;
}

/**
 * Inferred shape of the backend error body. The v0.5.0 contract uses
 * `code` as the discriminator; `detail` carries human-readable text.
 * (FastAPI uses `detail`; tensaw_base wraps it.) Either may be missing.
 */
interface ErrorBody {
  code?: string;
  detail?: string | { code?: string; message?: string };
  message?: string;
}

function extractCode(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const b = body as ErrorBody;
  if (typeof b.code === "string") return b.code;
  if (typeof b.detail === "object" && b.detail !== null && typeof b.detail.code === "string") {
    return b.detail.code;
  }
  return null;
}

function extractClinicId(url: string | undefined): number | null {
  if (!url) return null;
  const m = url.match(/\/v1\/clinics\/(\d+)/);
  return m ? Number(m[1]) : null;
}

// Track in-flight refresh so parallel 401s share one Amplify refresh round-trip.
let refreshLock: Promise<string | null> | null = null;

async function refreshIdToken(): Promise<string | null> {
  if (refreshLock) return refreshLock;
  refreshLock = (async () => {
    try {
      return await getIdToken({ forceRefresh: true });
    } finally {
      // Clear after the microtask so any callers awaiting this promise
      // resolve with this attempt's outcome, not the next one's.
      setTimeout(() => {
        refreshLock = null;
      }, 0);
    }
  })();
  return refreshLock;
}

export function initApiClient(config: ApiClientConfig): AxiosInstance {
  authBypassActive = config.authBypass;
  client = axios.create({
    baseURL: config.baseURL,
    timeout: 30_000,
    headers: { "Content-Type": "application/json" },
  });

  if (authBypassActive) {
    // Dev path: static bearer; no refresh, no error mapping.
    client.interceptors.request.use((reqConfig) => {
      reqConfig.headers.Authorization = "Bearer dev-bypass-token";
      return reqConfig;
    });
    return client;
  }

  // ----- Cognito mode -----

  // Request interceptor: attach the current ID token.
  client.interceptors.request.use(async (reqConfig: InternalAxiosRequestConfig) => {
    const token = await getIdToken();
    if (token) {
      reqConfig.headers.set?.("Authorization", `Bearer ${token}`);
      // Fallback for any consumer not using AxiosHeaders.set
      reqConfig.headers.Authorization = `Bearer ${token}`;
    }
    return reqConfig;
  });

  // Response interceptor: map errors per the v0.5.0 contract.
  client.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const status = error.response?.status;
      const body = error.response?.data as unknown;
      const code = extractCode(body);
      const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

      // ---- 401 path ----
      if (status === 401) {
        const isExpired = code === "TENSAW_BASE_ACCESS_TOKEN_EXPIRED";
        if (isExpired && original && !original._retried) {
          original._retried = true;
          const fresh = await refreshIdToken();
          if (fresh) {
            // Update header for the retry; the request interceptor would
            // also do it, but we're past that for `original`.
            original.headers.set?.("Authorization", `Bearer ${fresh}`);
            original.headers.Authorization = `Bearer ${fresh}`;
            return client!.request(original);
          }
          // Refresh itself failed → terminal.
          throw new TerminalAuthError(
            code ?? "TENSAW_BASE_ACCESS_TOKEN_EXPIRED",
            "Couldn't refresh the session. Please sign in again.",
          );
        }
        // Non-EXPIRED 401, or retry also 401'd → terminal.
        throw new TerminalAuthError(
          code ?? "TENSAW_BASE_ACCESS_TOKEN_INVALID",
          "Your session is no longer valid.",
        );
      }

      // ---- 403 path ----
      if (status === 403) {
        const clinicId = extractClinicId(original?.url);
        const kind = clinicId !== null ? "clinic" : "role";
        throw new AccessDeniedError(kind, clinicId);
      }

      // ---- 503 path ----
      if (status === 503 && code === "TENSAW_BASE_UPSTREAM_UNAVAILABLE") {
        throw new UpstreamUnavailableError(code);
      }

      // Everything else: pass through unchanged.
      return Promise.reject(error);
    },
  );

  return client;
}

export function getApiClient(): AxiosInstance {
  if (client === null) {
    throw new Error("API client not initialized. Call initApiClient() first.");
  }
  return client;
}

/** True iff this build is running with VITE_AUTH_BYPASS=true. */
export function isAuthBypassActive(): boolean {
  return authBypassActive;
}
